import { EikoLogo } from '@/components/EikoLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useResetPassword } from '@/hooks/useResetPassword';
import { useValidateResetToken } from '@/hooks/useValidateResetToken';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial'
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const inputClassName = cn(
  'h-11 rounded-lg border-stone-200 bg-white',
  'text-stone-900 placeholder:text-stone-400',
  'transition-all duration-300',
  'focus-visible:border-emerald-500/50',
  'focus-visible:ring-[3px] focus-visible:ring-emerald-500/15',
  'focus-visible:bg-white',
  'hover:border-stone-300',
);

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-stone-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-[420px] mx-4"
      >
        <div className="rounded-2xl border border-stone-200 p-8 sm:p-10 bg-white shadow-sm">
          <div className="flex flex-col">
            {children}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function ResetPasswordView() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { resetPassword, isLoading, isSuccess, error: resetError } = useResetPassword();
  const { data: isTokenValid, isLoading: isTokenValidating } =
    useValidateResetToken(token);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  const handleSubmit = (data: ResetPasswordFormData) => {
    resetPassword({ token, newPassword: data.newPassword });
  };

  // Validating token
  if (isTokenValidating) {
    return (
      <Shell>
        <div className="flex justify-center">
          <EikoLogo className="h-14 text-emerald-600" />
        </div>
        <div className="mt-6 flex flex-col items-center">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          <p className="mt-3 text-sm text-stone-600">Vérification du lien...</p>
        </div>
      </Shell>
    );
  }

  // Invalid / expired token
  if (isTokenValid === false) {
    return (
      <Shell>
        <div className="flex justify-center">
          <EikoLogo className="h-14 text-emerald-600" />
        </div>
        <div className="mt-6 flex flex-col items-center text-center">
          <XCircle className="h-12 w-12 text-red-400" />
          <h2 className="mt-4 text-lg font-medium text-stone-900">
            Lien invalide ou expiré
          </h2>
          <p className="mt-1.5 text-sm text-stone-600">
            Ce lien de réinitialisation n'est plus valide. Veuillez en demander un nouveau.
          </p>
        </div>
        <div className="mt-6 flex justify-center">
          <Link
            to="/forgot-password"
            className={cn(
              'inline-flex h-11 items-center justify-center rounded-lg px-6 text-sm font-semibold',
              'bg-emerald-600 text-white',
              'hover:bg-emerald-700',
              'transition-all duration-300',
            )}
          >
            Demander un nouveau lien
          </Link>
        </div>
      </Shell>
    );
  }

  // Success
  if (isSuccess) {
    return (
      <Shell>
        <div className="flex justify-center">
          <EikoLogo className="h-14 text-emerald-600" />
        </div>
        <div className="mt-6 flex flex-col items-center text-center">
          <CheckCircle className="h-14 w-14 text-emerald-500" />
          <h2 className="mt-4 text-lg font-medium text-stone-900">
            Mot de passe réinitialisé !
          </h2>
          <p className="mt-1.5 text-sm text-stone-600">
            Votre mot de passe a été mis à jour avec succès.
          </p>
        </div>
        <div className="mt-6 flex justify-center">
          <Link
            to="/login"
            className={cn(
              'inline-flex h-11 items-center justify-center rounded-lg px-6 text-sm font-semibold',
              'bg-emerald-600 text-white',
              'hover:bg-emerald-700',
              'transition-all duration-300',
            )}
          >
            Se connecter
          </Link>
        </div>
      </Shell>
    );
  }

  // Form
  return (
    <Shell>
      <div className="mb-8 flex justify-center">
        <EikoLogo className="h-14 text-emerald-600" />
      </div>

      <div className="mb-6 text-center">
        <h2 className="text-lg font-medium text-stone-900">
          Réinitialiser votre mot de passe
        </h2>
        <p className="mt-1.5 text-sm text-stone-600">
          Entrez votre nouveau mot de passe ci-dessous
        </p>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="newPassword" className="text-stone-700 text-sm">
              Nouveau mot de passe
            </Label>
            <Input
              {...form.register('newPassword')}
              id="newPassword"
              type="password"
              autoCapitalize="none"
              autoComplete="new-password"
              autoCorrect="off"
              disabled={isLoading}
              className={inputClassName}
            />
            {form.formState.errors.newPassword && (
              <p className="text-sm text-red-500">
                {form.formState.errors.newPassword.message}
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="confirmPassword" className="text-stone-700 text-sm">
              Confirmer le mot de passe
            </Label>
            <Input
              {...form.register('confirmPassword')}
              id="confirmPassword"
              type="password"
              autoCapitalize="none"
              autoComplete="new-password"
              autoCorrect="off"
              disabled={isLoading}
              className={inputClassName}
            />
            {form.formState.errors.confirmPassword && (
              <p className="text-sm text-red-500">
                {form.formState.errors.confirmPassword.message}
              </p>
            )}
          </div>

          {resetError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 border border-red-200">
              {resetError.message || 'Échec de la réinitialisation. Veuillez réessayer.'}
            </div>
          )}

          <div className="pt-2">
            <Button
              type="submit"
              disabled={isLoading}
              className={cn(
                'relative w-full h-11 rounded-lg text-sm font-semibold',
                'bg-emerald-600 text-white',
                'hover:bg-emerald-700',
                'transition-all duration-300',
                'disabled:opacity-50',
              )}
            >
              {isLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Réinitialiser le mot de passe'
              )}
            </Button>
          </div>
        </div>
      </form>
    </Shell>
  );
}
